import { supabase } from '@/integrations/supabase/client';
import { capitalizeText } from '@/lib/utils';

export interface EnrollmentData {
  studentFirstName: string;
  studentLastName: string;
  studentClass: string;
  parentEmail: string;
  parentPhone: string;
  activityId: string;
  billingType?: 'per_session' | 'monthly';
  selectedDays?: number[]; // Para actividades que requieren selección de días
}

interface EnrollmentResult {
  student: any;
  enrollment: any;
  schedule?: any[];
  isNewStudent: boolean;
  message: string;
}

// Función para normalizar texto (quitar espacios, convertir a minúsculas)
const normalizeText = (text: string): string => {
  return text.trim().toLowerCase();
};

// Función para normalizar teléfono (quitar espacios, guiones, etc.)
const normalizePhone = (phone: string): string => {
  return phone.replace(/\s|-|\(|\)/g, '');
};

// Buscar estudiante existente SOLO por nombre + apellidos + clase
export const findExistingStudent = async (data: EnrollmentData) => {
  const normalizedFirstName = normalizeText(capitalizeText(data.studentFirstName));
  const normalizedLastName = normalizeText(capitalizeText(data.studentLastName));
  const normalizedClass = normalizeText(data.studentClass);

  const { data: studentsByName, error: nameError } = await supabase
    .from('students')
    .select('*')
    .ilike('first_name', normalizedFirstName)
    .ilike('last_name', normalizedLastName)
    .ilike('class', normalizedClass);

  if (nameError) throw nameError;

  if (studentsByName && studentsByName.length > 0) {
    return { student: studentsByName[0], matchType: 'name_class' };
  }

  return null;
};

// Crear nuevo estudiante
export const createNewStudent = async (data: EnrollmentData) => {
  const capitalizedFirstName = capitalizeText(data.studentFirstName);
  const capitalizedLastName = capitalizeText(data.studentLastName);
  
  const studentData = {
    name: `${capitalizedFirstName} ${capitalizedLastName}`,
    first_name: capitalizedFirstName,
    last_name: capitalizedLastName,
    class: data.studentClass.trim(),
    email: data.parentEmail.trim().toLowerCase(),
    phone: normalizePhone(data.parentPhone),
    default_billing_type: 'monthly' as const,
  };

  const { data: newStudent, error } = await supabase
    .from('students')
    .insert(studentData)
    .select()
    .single();

  if (error) throw error;
  return newStudent;
};

// Actualizar datos del estudiante si es necesario
export const updateStudentIfNeeded = async (existingStudent: any, data: EnrollmentData) => {
  const updates: any = {};
  let needsUpdate = false;

  const newFirstName = capitalizeText(data.studentFirstName);
  const newLastName = capitalizeText(data.studentLastName);
  const newFullName = `${newFirstName} ${newLastName}`;
  
  if (!existingStudent.first_name || existingStudent.first_name !== newFirstName) {
    updates.first_name = newFirstName;
    needsUpdate = true;
  }
  
  if (!existingStudent.last_name || existingStudent.last_name !== newLastName) {
    updates.last_name = newLastName;
    needsUpdate = true;
  }
  
  if (existingStudent.name !== newFullName) {
    updates.name = newFullName;
    needsUpdate = true;
  }

  const newEmail = data.parentEmail.trim().toLowerCase();
  if (!existingStudent.email || existingStudent.email !== newEmail) {
    updates.email = newEmail;
    needsUpdate = true;
  }

  const newPhone = normalizePhone(data.parentPhone);
  if (!existingStudent.phone || existingStudent.phone !== newPhone) {
    updates.phone = newPhone;
    needsUpdate = true;
  }

  if (needsUpdate) {
    const { data: updatedStudent, error } = await supabase
      .from('students')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', existingStudent.id)
      .select()
      .single();

    if (error) throw error;
    return updatedStudent;
  }

  return existingStudent;
};

// Verificar si ya está inscrito en la actividad
export const checkExistingEnrollment = async (studentId: string, activityId: string) => {
  const { data: enrollment, error } = await supabase
    .from('student_activities')
    .select('*')
    .eq('student_id', studentId)
    .eq('activity_id', activityId)
    .eq('status', 'active')
    .maybeSingle();

  if (error) throw error;
  return enrollment;
};

// Crear inscripción en la actividad
export const createEnrollment = async (
  studentId: string, 
  activityId: string, 
  billingType?: 'per_session' | 'monthly'
) => {
  let effectiveBillingType = billingType;
  
  if (!effectiveBillingType) {
    const { data: student, error: studentError } = await supabase
      .from('students')
      .select('default_billing_type')
      .eq('id', studentId)
      .single();
    
    if (studentError) {
      effectiveBillingType = 'per_session';
    } else {
      effectiveBillingType = student.default_billing_type || 'per_session';
    }
  }

  const enrollmentData = {
    student_id: studentId,
    activity_id: activityId,
    status: 'active',
    billing_type_override: effectiveBillingType,
  };

  const { data: enrollment, error } = await supabase
    .from('student_activities')
    .insert(enrollmentData)
    .select()
    .single();

  if (error) throw error;
  return enrollment;
};

// Crear horario específico para un estudiante en una actividad
export const createStudentSchedule = async (
  studentId: string, 
  activityId: string, 
  selectedDays: number[]
) => {
  const scheduleData = selectedDays.map(day => ({
    student_id: studentId,
    activity_id: activityId,
    day_of_week: day
  }));

  const { data: schedule, error } = await supabase
    .from('student_activity_schedule')
    .insert(scheduleData)
    .select();

  if (error) throw error;
  return schedule;
};

// Verificar cupos disponibles
export const checkActivityCapacity = async (activityId: string) => {
  const { data: activity, error: activityError } = await supabase
    .from('activities')
    .select('name, max_capacity')
    .eq('id', activityId)
    .single();

  if (activityError) throw activityError;

  const { count: currentEnrollments, error: countError } = await supabase
    .from('student_activities')
    .select('*', { count: 'exact', head: true })
    .eq('activity_id', activityId)
    .eq('status', 'active');

  if (countError) throw countError;

  const maxCapacity = activity.max_capacity || 999;
  const hasCapacity = currentEnrollments! < maxCapacity;

  return {
    activity,
    currentEnrollments: currentEnrollments || 0,
    maxCapacity,
    hasCapacity,
  };
};

// Función principal consolidada para inscribir estudiante
export const enrollStudent = async (data: EnrollmentData): Promise<EnrollmentResult> => {
  try {
    // 1. Verificar cupos disponibles
    const capacityInfo = await checkActivityCapacity(data.activityId);
    
    if (!capacityInfo.hasCapacity) {
      console.log('⚠️ Actividad sin cupos disponibles, inscribiendo en lista de espera');
    }

    // 2. Buscar estudiante existente
    const existingStudentResult = await findExistingStudent(data);
    
    let student;
    let isNewStudent = false;

    if (existingStudentResult) {
      student = await updateStudentIfNeeded(existingStudentResult.student, data);
    } else {
      student = await createNewStudent(data);
      isNewStudent = true;
    }

    // 3. Verificar si ya está inscrito en esta actividad
    const existingEnrollment = await checkExistingEnrollment(student.id, data.activityId);
    
    if (existingEnrollment) {
      throw new Error(`${student.first_name} ${student.last_name} ya está inscrito/a en esta actividad`);
    }

    // 4. Crear la inscripción
    const enrollment = await createEnrollment(student.id, data.activityId, data.billingType);

    // 5. Si hay días específicos seleccionados, crear el horario
    let schedule = null;
    if (data.selectedDays && data.selectedDays.length > 0) {
      schedule = await createStudentSchedule(student.id, data.activityId, data.selectedDays);
    }

    const message = isNewStudent 
      ? `${student.first_name} ${student.last_name} ha sido registrado/a como nuevo/a estudiante e inscrito/a en la actividad`
      : `${student.first_name} ${student.last_name} ha sido inscrito/a en la actividad`;

    return {
      student,
      enrollment,
      schedule,
      isNewStudent,
      message,
    };

  } catch (error: any) {
    throw new Error(error.message || 'Error desconocido en el proceso de inscripción');
  }
};